import React from 'react';
import { Link } from 'react-router-dom';
import { QueryResult } from '../models/QueryResult';

interface QueryInfoBoxProps {
   query: QueryResult;
}

function stateKind(raw: string | undefined): 'holo' | 'apo' | 'other' | null {
   if (!raw) return null;
   const s = raw.trim().toLowerCase();
   if (!s) return null;
   if (s.startsWith('h')) return 'holo';
   if (s.startsWith('a')) return 'apo';
   return 'other';
}

function formatMethod(method: string | undefined): string {
   if (!method) return '';
   const spaced = method.replace(/_/g, ' ').toLowerCase();
   return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Compact summary of the structure, ligand, and result counts that produced
 * this job query or database entry. Built from fields the result payload
 * already carries.
 */
const QueryInfoBox = ({ query }: QueryInfoBoxProps): React.JSX.Element => {
   const pocket = query.query_pockets?.[0];
   const pdb = (query.target_pdb_id || pocket?.pdb_id || '').toUpperCase();
   const chains = (pocket?.chains ?? []).filter(Boolean);
   const ligands = (pocket?.ligands ?? []).map((ligand) => ligand.trim()).filter(Boolean);
   const pocketUniprots = pocket?.uniprot_ids ?? [];
   const uniprots =
      pocketUniprots.length > 0 ? pocketUniprots : (query.ubs?.uniprot_infos ?? []).map((info) => info.uniprot_id);
   const kind = stateKind(pocket?.apoholo_assignment);
   const method = formatMethod(pocket?.experimental_method);
   const resolution = pocket?.resolution;
   const sasa = pocket?.sasa;
   const sasaAvg = pocket?.sasa_avg;
   const sites = query.num_query_pockets ?? 0;

   return (
      <section className="query-info-box" aria-label="Query info">
         <dl className="qi-list">
            {pdb && (
               <div className="qi-row">
                  <dt>Structure</dt>
                  <dd>
                     <a
                        href={`https://www.rcsb.org/structure/${pdb}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="qi-mono"
                     >
                        {pdb}
                     </a>
                     {chains.length > 0 && (
                        <>
                           <span className="qi-sep">·</span>
                           chain <span className="qi-mono">{chains.join(', ')}</span>
                        </>
                     )}
                  </dd>
               </div>
            )}
            {ligands.length > 0 && (
               <div className="qi-row">
                  <dt>Ligand</dt>
                  <dd>
                     <a
                        href={`https://www.rcsb.org/ligand/${ligands[0]}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="qi-mono"
                     >
                        {ligands.join(', ')}
                     </a>
                  </dd>
               </div>
            )}
            {uniprots.length > 0 && (
               <div className="qi-row">
                  <dt>UniProt</dt>
                  <dd>
                     {uniprots.map((id, index) => (
                        <span className="qi-uniprot" key={id}>
                           {index > 0 && <span className="qi-sep">·</span>}
                           <a
                              href={`https://www.uniprot.org/uniprotkb/${id}/entry`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="qi-mono"
                           >
                              {id}
                           </a>
                           <Link to={`/db/ubs/${id}`} className="qi-ubs" title={`Unified binding sites for ${id}`}>
                              UBS
                           </Link>
                        </span>
                     ))}
                  </dd>
               </div>
            )}
            {kind && (
               <div className="qi-row">
                  <dt>Query state</dt>
                  <dd>
                     <span className={`qi-badge qi-badge--${kind}`} title="Whether the query pocket has a ligand bound">
                        {kind === 'other' ? pocket?.apoholo_assignment : kind}
                     </span>
                  </dd>
               </div>
            )}
            {(method || resolution != null) && (
               <div className="qi-row">
                  <dt>Method</dt>
                  <dd>
                     {method}
                     {method && resolution != null && <span className="qi-sep">·</span>}
                     {resolution != null && <>{resolution.toFixed(2)} Å</>}
                  </dd>
               </div>
            )}
            {sasa != null && (
               <div className="qi-row">
                  <dt>Pocket SASA</dt>
                  <dd title="Solvent-accessible surface area of the pocket residues">
                     {sasa.toFixed(1)}
                     {sasaAvg != null && <span className="qi-dim"> (avg {sasaAvg.toFixed(1)})</span>}
                  </dd>
               </div>
            )}
            <div className="qi-row">
               <dt>Results</dt>
               <dd>
                  <span className="qi-badge qi-badge--holo">{query.num_found_holo_pdbs} holo</span>
                  <span className="qi-badge qi-badge--apo">{query.num_found_apo_pdbs} apo</span>
                  {query.num_found_unobserved_pdbs > 0 && (
                     <span className="qi-badge qi-badge--other">{query.num_found_unobserved_pdbs} unobs.</span>
                  )}
                  {query.num_af_sessions > 0 && (
                     <span className="qi-badge qi-badge--af">{query.num_af_sessions} AF</span>
                  )}
                  <div className="qi-dim">
                     {sites} query site{sites === 1 ? '' : 's'} · chains: {query.num_found_holo_chains} holo /{' '}
                     {query.num_found_apo_chains} apo
                  </div>
               </dd>
            </div>
         </dl>
      </section>
   );
};

export default QueryInfoBox;
